'user client';

import React, { useEffect, useRef } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale } from 'next-intl';
import styled from 'styled-components';

import { isBreakpoint, device } from '@/ui/styles/breakpoints';
import { addLocalePath } from '@/utils/urlBuilder/utils';

const ContentsWrap = styled.div`
  @media ${device.max.md} {
    padding-bottom: 20px;
  }

  h4 {
    font-size: 13px;
    line-height: 20px;
    margin: 0;
    padding: 0 0 10px;
  }

  > ul {
    font-size: 13px;
    line-height: 20px;
    margin: 0;
    padding: 0;

    > li {
      border-top: 1px solid #dedede;
    }
  }

  ol {
    border-top: 1px solid #dedede;
    counter-reset: ol;
    margin: 10px 0 0;
    padding: 10px 0 10px 10px;
  }

  li {
    display: block;
    margin-bottom: 0;
    padding-top: 10px;

    &:not(:last-child) {
      padding-bottom: 10px;
    }
  }

  a {
    color: inherit;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

type IsActiveProps = {
  $isActive?: boolean;
};

const Li = styled.li<IsActiveProps>`
  color: ${props =>
    props.$isActive ? props.theme.palette.primary.dark : '#6a6a6a'};
  font-weight: ${props => (props.$isActive ? 'bold' : 'normal')};

  &:before {
    counter-increment: ol;
    content: counter(ol) '. ';
  }
`;

export default function Contents({
  activeStep,
  guide,
  categories,
  siblings,
  steps,
  updateSidebar,
  sidebar,
}) {
  const locale = useLocale();
  const router = useRouter();
  const stepRefs = useRef(steps.map(() => React.createRef()));

  useEffect(() => {
    if (sidebar) {
      if (isBreakpoint(device.min.lg)) {
        if (
          stepRefs.current[activeStep] &&
          stepRefs.current[activeStep].current
        ) {
          stepRefs.current[activeStep].current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }
    }
  }, [activeStep, stepRefs, sidebar]);

  return (
    <ContentsWrap>
      <h4>
        {
          categories
            .flatMap(c => c.childrens)
            .find(ch => ch.slug === guide.categories[0]).title
        }
      </h4>
      <ul>
        {siblings.map(li => (
          <li key={li.id}>
            {guide.id === li.id ? (
              <>
                <Link href={addLocalePath(locale, `guide/${guide.slug}`)}>
                  <strong>
                    {guide.translations_info[locale].object_title}
                  </strong>
                </Link>
                <ol>
                  {steps.map((s, i) => (
                    <Li
                      $isActive={s.id === steps[activeStep].id}
                      key={s.id}
                      ref={stepRefs.current[i]}
                    >
                      {/* https://github.com/netlify/next-runtime/issues/2089#issuecomment-1689892702
                      <Link href={`/guide/${li.slug}#${s.id}`}>
                        {s.title}
                      </Link> */}
                      <a
                        onClick={() => {
                          router.push(
                            `${location.pathname}${s.id ? `#${s.id}` : ''}`
                          );
                          if (isBreakpoint(device.max.md)) {
                            updateSidebar(null);
                          }
                        }}
                      >
                        {s.title}
                      </a>
                    </Li>
                  ))}
                </ol>
              </>
            ) : (
              <Link href={addLocalePath(locale, `guide/${li.slug}`)}>
                {li.title}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </ContentsWrap>
  );
}
